/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect, useCallback } from "react";
import clsx from "clsx";
import { GblContext } from "providers/formatter";
import { Loading, useRedirect, useTranslate } from "react-admin";
import {
  Paper,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  LinearProgress,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import ListAltIcon from "@material-ui/icons/ListAlt";
import RssFeedIcon from "@material-ui/icons/RssFeed";
import ViewWeekIcon from "@material-ui/icons/ViewWeek";
import GridOnIcon from "@material-ui/icons/GridOn";
import TouchAppIcon from "@material-ui/icons/TouchApp";

import { getCountingDocumentDetail, voidCountingDocument } from "services/asset";
import { showReportByName } from "pages/Report/services";
import SnackbarUtils from "utils/SnackbarUtils";
import ActionMenu from "components/ActionMenu";
import { permissionName } from "utils/constants";

import ActivityLogDialog from "./ActivityLogDialog";
import PhotoGalleryDialog from "./PhotoGalleryDialog";
import ChangeLogDialog from "./ChangeLogDialog";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  actionsGroup: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  button: {
    textTransform: "none",
  },
  metadataCard: {
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
  },
  metadataItem: {
    padding: theme.spacing(2),
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
  },
  value: {
    fontWeight: 600,
    fontSize: "1rem",
  },
  progressBarContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  progressBar: {
    flexGrow: 1,
    height: 10,
    borderRadius: 5,
  },
  tableContainer: {
    marginTop: theme.spacing(3),
    boxShadow: theme.shadows[2],
  },
  tableHeader: {
    backgroundColor: theme.palette.grey[100],
    fontWeight: "bold",
  },
  photoButton: {
    color: "#2196f3",
  },
  statusDraft: {
    color: "#ff9800",
    fontWeight: "bold",
  },
  statusCommitted: {
    color: "#4caf50",
    fontWeight: "bold",
  },
  statusVoid: {
    color: "#f44336",
    fontWeight: "bold",
  },
}));

const Detail = (props) => {
  const { id, basePath, permissions } = props;
  const translate = useTranslate();
  const redirect = useRedirect();
  const classes = useStyles();
  const { DateToString } = useContext(GblContext);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // Dialog states
  const [logOpen, setLogOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryPhotoUrl, setGalleryPhotoUrl] = useState("");
  const [galleryAssetName, setGalleryAssetName] = useState("");
  const [changeLogOpen, setChangeLogOpen] = useState(false);
  const [changeLogAssetId, setChangeLogAssetId] = useState("");
  const [tableSearchText, setTableSearchText] = useState("");

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCountingDocumentDetail(id);
      if (response) {
        setData(response);
      } else {
        SnackbarUtils.error("Failed to load document details");
      }
    } catch (e) {
      SnackbarUtils.error(e.message || "Error fetching details");
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchDetail();
    }
  }, [id]);

  const handlePrint = () => {
    showReportByName("AST_CountingDocument", [{ Name: "DocNo", Value: id }]);
  };

  const handleVoid = () => {
    if (data?.Status !== "Draft") return;
    
    const title = translate("ra.question.confirmVoid", { module: "Counting Document" }) || "Void Counting Document?";
    const msg = "This document will be cancelled and locked. Are you sure you want to void?";
    
    SnackbarUtils.confirm(msg, async () => {
      try {
        const response = await voidCountingDocument(id);
        if (response) {
          SnackbarUtils.success("Document voided successfully");
          fetchDetail();
        } else {
          SnackbarUtils.error("Failed to void document");
        }
      } catch (e) {
        SnackbarUtils.error(e.message || "Failed to void document");
      }
    }, title);
  };

  if (loading && !data) {
    return <Loading />;
  }

  if (!data) {
    return (
      <Paper className={classes.root}>
        <Typography variant="h6" color="error">
          Document not found
        </Typography>
      </Paper>
    );
  }

  // Calculate physical check progress
  const assetCounts = data.asset_counts || data.AssetCounts || [];

  const filteredAssetCounts = assetCounts.filter((item) => {
    const assetId = (item.asset_id || item.AssetId || "").toLowerCase();
    const assetName = (item.asset_name || item.AssetName || item.Description || item.description || "").toLowerCase();
    const remark = (item.remark || item.Remark || item.comment || item.Comment || "").toLowerCase();
    const search = tableSearchText.toLowerCase();
    return assetId.includes(search) || assetName.includes(search) || remark.includes(search);
  });
  const totalAssets = assetCounts.length;
  const auditedAssets = assetCounts.filter(
    (item) => (item.counted_qty !== null && item.counted_qty !== undefined && item.counted_qty >= 0)
  ).length;
  const progressPercent = totalAssets > 0 ? Math.round((auditedAssets / totalAssets) * 100) : 0;

  const renderScanMethod = (method) => {
    const normMethod = (method || "").toLowerCase();
    let icon = <TouchAppIcon fontSize="small" />;
    let text = "Manual";

    if (normMethod.includes("qr")) {
      icon = <GridOnIcon fontSize="small" />;
      text = "QR Code";
    } else if (normMethod.includes("rfid")) {
      icon = <RssFeedIcon fontSize="small" />;
      text = "RFID";
    } else if (normMethod.includes("barcode") || normMethod.includes("bar")) {
      icon = <ViewWeekIcon fontSize="small" />;
      text = "Barcode";
    }

    return (
      <Box display="flex" alignItems="center" gap={0.5}>
        {icon}
        <Typography variant="body2">{text}</Typography>
      </Box>
    );
  };

  const getStatusClass = (status) => {
    if (status === "Draft") return classes.statusDraft;
    if (status === "Committed") return classes.statusCommitted;
    if (status === "Void") return classes.statusVoid;
    return "";
  };

  const menuControlProp = [
    { name: "Back", fnc: () => redirect("list", basePath) },
    {
      name: "Activity Log",
      fnc: () => setLogOpen(true),
    },
    {
      name: "Print",
      fnc: handlePrint,
    },
    {
      name: "Void",
      fnc: handleVoid,
      hide: data.Status !== "Draft",
    },
  ];

  return (
    <Box className={classes.root}>
      <ActionMenu
        menuControl={menuControlProp}
        permission={permissions?.find((i) => i.Name === permissionName["Ast.CountingDocument"])}
      />

      {/* Metadata Card */}
      <Card className={classes.metadataCard}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3} className={classes.metadataItem}>
              <Typography className={classes.label}>{translate("ra.field.Doc No")}</Typography>
              <Typography className={classes.value}>{data.DocNo || data.doc_no}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.metadataItem}>
              <Typography className={classes.label}>{translate("ra.field.Location")}</Typography>
              <Typography className={classes.value}>{data.Location || data.location}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.metadataItem}>
              <Typography className={classes.label}>{translate("ra.field.Count Date")}</Typography>
              <Typography className={classes.value}>
                {data.CountDate ? DateToString(new Date(data.CountDate)) : "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.metadataItem}>
              <Typography className={classes.label}>{translate("ra.field.Status")}</Typography>
              <Typography className={clsx(classes.value, getStatusClass(data.Status))}>
                {data.Status || data.status}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.metadataItem}>
              <Typography className={classes.label}>{translate("ra.field.Commit Date")}</Typography>
              <Typography className={classes.value}>
                {data.Status === "Committed" && data.CommitDate ? DateToString(new Date(data.CommitDate)) : "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.metadataItem}>
              <Typography className={classes.label}>Created By</Typography>
              <Typography className={classes.value}>
                {data.CreatedByName || data.CreatedBy || "-"}
                {data.CreatedByEmail && (
                  <Typography variant="caption" display="block" color="textSecondary">
                    {data.CreatedByEmail}
                  </Typography>
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={6} className={classes.metadataItem}>
              <Typography className={classes.label}>Audit Progress</Typography>
              <Box className={classes.progressBarContainer}>
                <LinearProgress
                  variant="determinate"
                  value={progressPercent}
                  className={classes.progressBar}
                  color={progressPercent === 100 ? "primary" : "secondary"}
                />
                <Typography variant="body2" style={{ fontWeight: "bold" }}>
                  {progressPercent}% ({auditedAssets}/{totalAssets})
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Asset Counts Table */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Audited Asset Counts
          </Typography>
          <TextField
            placeholder="Search assets..."
            variant="outlined"
            size="small"
            value={tableSearchText}
            onChange={(e) => setTableSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            style={{ width: 250 }}
          />
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader}>Asset ID</TableCell>
              <TableCell className={classes.tableHeader}>Asset Name</TableCell>
              <TableCell className={classes.tableHeader} align="right">Remaining Qty</TableCell>
              <TableCell className={classes.tableHeader} align="right">Counted Qty</TableCell>
              <TableCell className={classes.tableHeader} align="right">Incident Qty</TableCell>
              <TableCell className={classes.tableHeader}>Incident Type</TableCell>
              <TableCell className={classes.tableHeader}>Scan Method</TableCell>
              <TableCell className={classes.tableHeader}>Comment</TableCell>
              <TableCell className={classes.tableHeader} align="center">Change Log</TableCell>
              <TableCell className={classes.tableHeader} align="center">Photo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assetCounts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" style={{ padding: "20px" }}>
                  No asset counts synced for this document yet.
                </TableCell>
              </TableRow>
            ) : filteredAssetCounts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" style={{ padding: "20px" }}>
                  No matching assets found.
                </TableCell>
              </TableRow>
            ) : (
              filteredAssetCounts.map((item, index) => {
                const assetId = item.asset_id || item.AssetId || "";
                const assetName = item.asset_name || item.AssetName || item.Description || item.description || "";
                const remainingQty = item.remaining_qty !== undefined ? item.remaining_qty : (item.RemainingQty !== undefined ? item.RemainingQty : (item.BookQty || 0));
                const countedQty = item.counted_qty !== undefined ? item.counted_qty : (item.CountedQty || 0);
                const incidentQty = item.incident_qty !== undefined ? item.incident_qty : (item.IncidentQty || 0);
                const incidentCode = item.incident_type_code || item.IncidentTypeCode || "";
                const incidentReason = item.incident_reason || item.IncidentReason || "";
                const incidentStatus = item.incident_status || item.IncidentStatus || "PENDING";
                const scannedPhotoUrl = item.scanned_photo_url || item.ScannedPhotoUrl || "";
                const remark = item.remark || item.Remark || item.comment || item.Comment || "";
                const method = item.scan_method || item.ScanMethod || "";

                // Tooltip Content for Incident Details
                const incidentTooltip = (
                  <Box p={1}>
                    <Typography variant="body2" style={{ fontWeight: "bold" }}>
                      Incident Details:
                    </Typography>
                    <Typography variant="body2">
                      Status: {incidentStatus}
                    </Typography>
                    <Typography variant="body2">
                      Reason: {incidentReason || "No details provided"}
                    </Typography>
                  </Box>
                );

                return (
                  <TableRow key={item.id || index} hover>
                    <TableCell style={{ fontWeight: 500 }}>{assetId}</TableCell>
                    <TableCell>{assetName}</TableCell>
                    <TableCell align="right">{remainingQty}</TableCell>
                    <TableCell align="right" style={{ fontWeight: "bold", color: "#2196f3" }}>
                      {countedQty}
                    </TableCell>
                    <TableCell align="right" style={{ color: incidentQty !== 0 ? "#f44336" : "inherit" }}>
                      {incidentQty}
                    </TableCell>
                    <TableCell>
                      {incidentCode ? (
                        <Tooltip title={incidentTooltip} arrow interactive placement="top">
                          <Chip
                            label={incidentCode}
                            size="small"
                            color="secondary"
                            style={{
                              backgroundColor: incidentStatus === "DONE" ? "#4caf50" : "#ff9800",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>{renderScanMethod(method)}</TableCell>
                    <TableCell>{remark || "-"}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.currentTarget.blur();
                          setChangeLogAssetId(assetId);
                          setChangeLogOpen(true);
                        }}
                      >
                        <ListAltIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">
                      {scannedPhotoUrl ? (
                        <IconButton
                          size="small"
                          className={classes.photoButton}
                          onClick={(e) => {
                            e.currentTarget.blur();
                            setGalleryPhotoUrl(scannedPhotoUrl);
                            setGalleryAssetName(assetName);
                            setGalleryOpen(true);
                          }}
                        >
                          <PhotoCameraIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pop-up Modals */}
      <ActivityLogDialog
        open={logOpen}
        onClose={() => setLogOpen(false)}
        auditLogs={data.AuditLogs || data.audit_logs}
      />

      <PhotoGalleryDialog
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        photoUrl={galleryPhotoUrl}
        assetName={galleryAssetName}
      />

      <ChangeLogDialog
        open={changeLogOpen}
        onClose={() => setChangeLogOpen(false)}
        assetId={changeLogAssetId}
      />
    </Box>
  );
};

export default Detail;
